import type { OrderKind, PrinterOutput, PrintingSettings } from "../../api/types";

/**
 * "Wat drukt elke printer af?" (Furkan, 11/10/2026: one table instead of Werkwijze keuken, the copies per
 * printer and Afrekeningen): per printer and kind of order a choice, and how the settings follow from it.
 */

export const ORDER_KINDS: readonly OrderKind[] = ["takeaway", "delivery", "table"];

/** What a cell shows in its first select. "all": the kitchen ticket of every table with everything on it. */
export type What = "none" | "ticket" | "all" | "receipt" | "ticketReceipt" | "allReceipt";

/** The choice of one cell: what, how many afrekeningen, and (afhaal, levering) only when it makes something. */
export interface Choice {
  what: What;
  count: number;
  onlyIfMakes: boolean;
}

export const NOTHING: PrinterOutput = { kitchen: "none", receipt: 0, ifMakes: 0, copy: 0 };

/** The choices a cell offers: "alles" only for tables. */
export function whatsFor(kind: OrderKind): What[] {
  return kind === "table"
    ? ["none", "ticket", "all", "receipt", "ticketReceipt", "allReceipt"]
    : ["none", "ticket", "receipt", "ticketReceipt"];
}

export const withReceipt = (what: What) => what === "receipt" || what === "ticketReceipt" || what === "allReceipt";

/** A cell as its choice. What the choice cannot show (from before 11/10/2026) is in {@link leftOver}. */
export function choiceOf(cell: PrinterOutput): Choice {
  const afrekening = cell.receipt > 0 ? cell.receipt : cell.ifMakes;
  const onlyIfMakes = cell.receipt === 0 && cell.ifMakes > 0;
  const count = afrekening > 0 ? afrekening : 1;
  if (cell.kitchen === "none") return { what: afrekening > 0 ? "receipt" : "none", count, onlyIfMakes };
  // a kitchen ticket with an afrekening "als er iets voor hem bij is" is not offered: the ticket is shown
  if (cell.receipt === 0) return { what: cell.kitchen, count: 1, onlyIfMakes: false };
  return { what: cell.kitchen === "all" ? "allReceipt" : "ticketReceipt", count, onlyIfMakes: false };
}

/** What a cell prints beyond its choice: copies from before 11/10/2026, shown under it until it is changed. */
export function leftOver(cell: PrinterOutput): { copy: number; ifMakes: number } {
  return { copy: cell.copy, ifMakes: cell.receipt > 0 || cell.kitchen !== "none" ? cell.ifMakes : 0 };
}

export function cellOf(choice: Choice, kind: OrderKind): PrinterOutput {
  const kitchen = choice.what === "ticket" || choice.what === "ticketReceipt" ? "ticket" : "none";
  const all = choice.what === "all" || choice.what === "allReceipt";
  const afrekening = withReceipt(choice.what) ? choice.count : 0;
  const ifMakes = choice.what === "receipt" && choice.onlyIfMakes && kind !== "table";
  return {
    kitchen: all ? "all" : kitchen,
    receipt: ifMakes ? 0 : afrekening,
    ifMakes: ifMakes ? afrekening : 0,
    copy: 0,
  };
}

const prints = (settings: PrintingSettings, printerId: string, kind: OrderKind) =>
  (settings.output[printerId]?.[kind].receipt ?? 0) > 0;

/**
 * The settings with one cell chosen. The kassabon stays where it is while that printer prints the
 * afrekening; otherwise it goes to the first printer that does (as the server keeps it).
 */
export function choose(
  settings: PrintingSettings,
  printerIds: readonly string[],
  printerId: string,
  kind: OrderKind,
  choice: Choice,
): PrintingSettings {
  const cells = settings.output[printerId] ?? { takeaway: NOTHING, delivery: NOTHING, table: NOTHING };
  const output = { ...settings.output, [printerId]: { ...cells, [kind]: cellOf(choice, kind) } };
  const next = { ...settings, output };
  const current = settings.kassabon[kind];
  const kassabon =
    current !== null && prints(next, current, kind)
      ? current
      : (printerIds.find((id) => prints(next, id, kind)) ?? null);
  return { ...next, kassabon: { ...settings.kassabon, [kind]: kassabon } };
}

/** The printers that make something (Keukenbonnen per product). */
export function routedPrinters(kitchen: PrintingSettings["kitchen"]): Set<string> {
  return new Set([...Object.values(kitchen.categories).flat(), ...Object.values(kitchen.products).flat()]);
}

/** A printer that makes products but prints nothing at all for this kind of order. */
export function printsNothing(settings: PrintingSettings, printerId: string, kind: OrderKind): boolean {
  const cell = settings.output[printerId]?.[kind] ?? NOTHING;
  return (
    routedPrinters(settings.kitchen).has(printerId) &&
    cell.kitchen === "none" &&
    cell.receipt + cell.ifMakes + cell.copy === 0
  );
}
